import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/data";
import { NAV } from "@/constants/testIds";
import { useScrolled } from "@/lib/hooks";

const LINKS = [
    { to: "/", label: "Home", testid: NAV.linkHome },
    { to: "/about", label: "About", testid: NAV.linkAbout },
    { to: "/collections", label: "Collections", testid: NAV.linkCollections },
];

export default function Navbar() {
    const scrolled = useScrolled(40);
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const solid = scrolled || location.pathname !== "/";

    const goToConsultation = () => {
        setOpen(false);
        if (location.pathname === "/") {
            const el = document.getElementById("consultation");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate("/#consultation");
        }
    };

    return (
        <header
            data-testid={NAV.root}
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                solid
                    ? "bg-[#0A0A0A]/90 backdrop-blur border-b border-[#1F1F1F]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
                {/* Left links (desktop) */}
                <nav className="hidden md:flex items-center gap-10 flex-1">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `overline gold-underline transition-colors text-[#F1F1EE] ${
                                    isActive ? "opacity-100" : "opacity-75 hover:opacity-100"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Center Logo */}
                <Link to="/" className="flex-shrink-0" data-testid={NAV.logo} aria-label="The Treasure Threads Home">
                    <img
                        src={BRAND.logo}
                        alt="The Treasure Threads"
                        className="h-14 md:h-16 w-auto transition-all duration-500"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                </Link>

                {/* Right side (desktop) */}
                <div className="hidden md:flex items-center justify-end gap-6 flex-1">
                    <button
                        type="button"
                        onClick={goToConsultation}
                        data-testid="nav-enquire-btn"
                        className="overline gold-underline text-[#F1F1EE]"
                    >
                        Enquire
                    </button>
                </div>

                {/* Mobile toggle */}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid={NAV.mobileToggle}
                    aria-label="Toggle menu"
                    className="md:hidden text-[#F1F1EE]"
                >
                    {open ? <X className="w-6 h-6" strokeWidth={1.4} /> : <Menu className="w-6 h-6" strokeWidth={1.4} />}
                </button>
            </div>

            {/* Mobile menu — rendered via portal to escape stacking contexts */}
            {open && typeof document !== "undefined" && createPortal(
                <div
                    data-testid={NAV.mobileMenu}
                    className="md:hidden fixed inset-0 flex flex-col"
                    style={{
                        zIndex: 60,
                        backgroundColor: "#050505",
                        paddingTop: "5rem",
                    }}
                >
                    {/* Subtle radial glow for premium depth */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(212,175,55,0.06), transparent 60%)",
                        }}
                    />

                    {/* Close button inside portal — the header X button lives above */}
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                        className="absolute top-6 right-6 text-[#F1F1EE] z-10 p-2"
                        data-testid="nav-mobile-close"
                    >
                        <X className="w-6 h-6" strokeWidth={1.4} />
                    </button>

                    <div className="relative z-10 px-8 pt-10 flex flex-col gap-8">
                        <img
                            src={BRAND.logo}
                            alt="The Treasure Threads"
                            width="56"
                            height="56"
                            className="h-14 w-14 mb-4 self-start object-contain"
                            style={{ filter: "brightness(0) invert(1)" }}
                        />
                        <nav className="flex flex-col gap-7">
                            {LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    data-testid={`${l.testid}-mobile`}
                                    className="font-serif-display text-4xl text-[#F1F1EE]"
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <div className="hairline my-2" />
                            <button
                                type="button"
                                onClick={goToConsultation}
                                data-testid="nav-enquire-btn-mobile"
                                className="text-left overline text-[#F1F1EE]"
                            >
                                Enquire
                            </button>
                        </nav>
                    </div>

                    <div className="mt-auto p-8 relative z-10">
                        <div className="hairline mb-6" />
                        <p className="overline text-[10px] text-[#666] mb-2">Contact</p>
                        <p className="text-sm text-[#F1F1EE] font-light">{BRAND.phone}</p>
                        <p className="text-sm text-[#A8A8A8] font-light break-all">{BRAND.email}</p>
                    </div>
                </div>,
                document.body
            )}
        </header>
    );
}
